import dayjs from "dayjs";

// Attendees can join from 10 minutes before the start until the event ends.
const JOIN_OPENS_BEFORE_START_MS = 10 * 60 * 1000;

export type EventTiming = {
  label: string;
  color: string;
  isLive: boolean;
  hasEnded: boolean;
};

export function getEventTiming(
  start: string,
  end: string,
  now: number
): EventTiming {
  const startsAt = dayjs(start);

  if (dayjs(end).valueOf() < now) {
    return { label: "Ended", color: "gray", isLive: false, hasEnded: true };
  }
  if (startsAt.valueOf() <= now) {
    return { label: "Live now", color: "red", isLive: true, hasEnded: false };
  }

  const daysAway = startsAt
    .startOf("day")
    .diff(dayjs(now).startOf("day"), "day");
  const label =
    daysAway === 0
      ? "Today"
      : daysAway === 1
      ? "Tomorrow"
      : `In ${daysAway} days`;

  return { label, color: "primary", isLive: false, hasEnded: false };
}

export function isJoinOpen(start: string, end: string, now: number) {
  return (
    now >= dayjs(start).valueOf() - JOIN_OPENS_BEFORE_START_MS &&
    now <= dayjs(end).valueOf()
  );
}

export function formatEventDate(start: string, end: string) {
  const startsAt = dayjs(start);
  const endsAt = dayjs(end);

  if (startsAt.isSame(endsAt, "day")) {
    return startsAt.format("ddd, D MMM YYYY");
  }
  if (startsAt.isSame(endsAt, "year")) {
    return `${startsAt.format("D MMM")} – ${endsAt.format("D MMM YYYY")}`;
  }
  return `${startsAt.format("D MMM YYYY")} – ${endsAt.format("D MMM YYYY")}`;
}

export function formatEventTime(start: string, end: string) {
  const time = `${dayjs(start).format("h:mm A")} – ${dayjs(end).format(
    "h:mm A"
  )}`;
  const zone = getTimeZoneName(start);

  return zone ? `${time} (${zone})` : time;
}

// Times are shown in the viewer's timezone, so label them with that zone
// (e.g. "GMT+7") instead of the event's free-text `timezone` field.
function getTimeZoneName(date: string) {
  try {
    return new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
      .formatToParts(new Date(date))
      .find((part) => part.type === "timeZoneName")?.value;
  } catch {
    return undefined;
  }
}
